import { createContext, useContext, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ROLES } from '../constants/roleConstants';
import { BASE_PATH_PREFIX, RAD_PROFILE, LIVE_AVAILABLE_SHIFTS, SCHEDULING_CALENDAR } from '../constants/routes';
import { saveThemePreference } from '../modules/login/services/LoginServices';
import { getCookie, setCookie } from '../store/localStorage';
import '../styles/ProviderTheme.css';

const ProviderThemeContext = createContext(null);
const providerPages = [BASE_PATH_PREFIX, RAD_PROFILE, LIVE_AVAILABLE_SHIFTS, SCHEDULING_CALENDAR];

export function ProviderThemeProvider({ userData, children }) {
    const { pathname } = useLocation();
    const isProvider = !!userData?.UserId && userData?.RoleCode === ROLES.PROVIDER;
    const enabled = isProvider && providerPages.some(path =>
        pathname === path || (path !== BASE_PATH_PREFIX && pathname.startsWith(`${path}/`))
    );
    // The saved preference comes with the login response (userDetailModel.IsDarkMode)
    // and lives in the userData cookie, so restoring it needs no API call.
    const [theme, setTheme] = useState(() => (isProvider && userData?.IsDarkMode === true ? 'dark' : 'light'));
    const [isSaving, setIsSaving] = useState(false);

    // The document scope includes date pickers and select menus portaled to body.
    useLayoutEffect(() => {
        if (enabled) document.documentElement.dataset.providerTheme = theme;
        else delete document.documentElement.dataset.providerTheme;
        return () => { delete document.documentElement.dataset.providerTheme; };
    }, [enabled, theme]);

    // Optimistic: apply immediately, persist on the server, revert if the save fails.
    const toggleTheme = async () => {
        if (isSaving) return;
        const previousTheme = theme;
        const nextTheme = previousTheme === 'dark' ? 'light' : 'dark';
        setTheme(nextTheme);
        setIsSaving(true);
        try {
            const isDarkMode = nextTheme === 'dark';
            const response = await saveThemePreference({ IsDarkMode: isDarkMode });
            const value = response?.values || response?.Values;
            const token = value?.TKN || value?.tkn;
            if (!(response?.Success ?? response?.success) || !token) {
                throw new Error(response?.ErrorMessage || response?.errorMessage || 'Theme preference was not saved.');
            }
            // The server re-issues the token (same expiry) with the new IsDarkMode claim.
            // Only the cookies are updated (not the Redux userData) so pages that depend
            // on userData don't refetch; a reload restores the theme from the cookie.
            const cookieExpiry = getCookie('jwtExpirationTime');
            setCookie('authToken', token, cookieExpiry);
            const storedUserData = getCookie('userData');
            if (storedUserData) setCookie('userData', { ...storedUserData, IsDarkMode: isDarkMode }, cookieExpiry);
        } catch (error) {
            console.error('Unable to save provider theme preference', error);
            setTheme(previousTheme);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ProviderThemeContext.Provider value={{ enabled, theme, toggleTheme, isSaving }}>
            {children}
        </ProviderThemeContext.Provider>
    );
}

export const useProviderTheme = () => useContext(ProviderThemeContext);
